import { signal } from "@preact/signals-react"
import { useEffect } from "react"

import { addNotification } from "../stores/notification-store"
import { fetchUserManifestsByAccessToken } from "../utils/api"

export const userManifestSignal = signal(null)
export const errorSignal = signal(null)
export const loadingSignal = signal(false)
export const modifiedSignal = signal(false) // allows a component to flag the data as modified so subsequent calls to the hook will reload it
export const parameterHashSignal = signal(null)

const fetchData = async (businessEntityId, config, accessToken) => {
    const hash = await import('object-hash').then(module => module.default || module)
    // if any parameters have changed then flag as modified so that the hook will reload the data
    const currentParametersHash = hash([businessEntityId, config, accessToken])
    if (currentParametersHash != parameterHashSignal.value) {
        modifiedSignal.value = true
        parameterHashSignal.value = currentParametersHash
    }

    // short-circuit if we have a pending request, are waiting for parameters or have already received an error
    if (loadingSignal.value || !businessEntityId || !config || !accessToken || errorSignal.value || !modifiedSignal.value) {
        return
    }
    
    // short-circuit if we already have the data and it's not flagged as modified
    if (userManifestSignal.value && !modifiedSignal.value) {
        return
    }

    try {
        loadingSignal.value = true
        userManifestSignal.value = await fetchUserManifestsByAccessToken(businessEntityId, config, accessToken)
    }
    catch (err) {
        errorSignal.value = err
        console.log(err)
        addNotification(`Unable to load user manifest: ${err}`)
    }
    finally {
        loadingSignal.value = false
        modifiedSignal.value = false
    }

}

export default function useUserManifest(businessEntityId, config, accessToken) {
    useEffect(() => {
        fetchData(businessEntityId, config, accessToken)
    }, [businessEntityId, config, accessToken])

    return {
        userManifestSignal, loadingSignal, errorSignal, modifiedSignal, fetchData
    }
}